/**
 * /field/ndt/jobs/:id — one of the technician's NDT jobs (GET
 * /ndt-inspection/field/my-jobs/:id): the job, its welds, and a fast "Add
 * weld" (POST /field/jobs/:id/welds). A weld opens its own screen for exam
 * entry. SNAKE_CASE throughout; lengths in meta.units.length.
 *
 * A caller not on the crew is answered 403 `not_on_crew`, shown as the API's
 * refusal rather than as "no welds".
 *
 * Writes are online only. A failed save says it was NOT saved, in the place
 * the form is, and keeps what was typed.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAddNdtFieldWeld, useNdtCatalog, useNdtFieldJob, useNdtWelders } from '../../../hooks/useNdtInspection';
import { ndtJobStatusSkin, ndtQualStatusSkin, ndtRefusal, ndtWeldStandingSkin, numberFromInput, type NdtRefusal } from '../../../lib/ndt-inspection';
import type { NdtCatalog, NdtCatalogB2, NdtFieldWeld, NdtWeldWrite } from '../../../types/ndt-inspection';
import { NDT_DATE_INPUT, NDT_INPUT, NdtField } from '../NdtBits';
import { NdtRefusalNotice } from '../NdtJobBits';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
} from '../../pressure-testing/field/PtFieldBits';
import { ndtFieldJobWhere, ndtFieldWeldProgress } from './NdtMyJobsScreen';
import { NdtFieldJobTickets } from './NdtFieldTickets';

export const NDT_FIELD_NOT_SAVED = 'Not saved — the server could not be reached. Check your signal and try again.';

const EMPTY_WELD = { weld_number: '', welder_id: '', diameter: '', wall: '', process: '', position: '', weld_date: '' };

function AddWeldForm({ jobId, unit, today, onDone }: { jobId: string; unit: string | null; today: string | null; onDone: () => void }) {
  const add = useAddNdtFieldWeld();
  const catalog = useNdtCatalog();
  const welders = useNdtWelders();
  const cat = catalog.data?.data as (NdtCatalog & NdtCatalogB2) | null | undefined;
  const [v, setV] = useState({ ...EMPTY_WELD, weld_date: today ?? '' });
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const set = (k: keyof typeof EMPTY_WELD) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusal(null);
    if (!v.weld_number.trim()) return setError('Weld number is required.');
    const diameter = numberFromInput(v.diameter);
    const wall = numberFromInput(v.wall);
    if (Number.isNaN(diameter) || Number.isNaN(wall)) return setError('Diameter and wall must be numbers.');
    const body: NdtWeldWrite = {
      weld_number: v.weld_number.trim(),
      welder_id: v.welder_id || null,
      diameter,
      wall,
      process: v.process || null,
      position: v.position || null,
      weld_date: v.weld_date || null,
    };
    add.mutate(
      { jobId, body },
      {
        onSuccess: (res) => {
          toast.success(`Weld ${res.data?.weld_number ?? v.weld_number.trim()} added`);
          setV({ ...EMPTY_WELD, weld_date: v.weld_date });
          onDone();
        },
        onError: (err) => setRefusal(ndtRefusal(err, NDT_FIELD_NOT_SAVED)),
      },
    );
  };

  return (
    <PtFieldCard className="px-4 py-4">
      <form onSubmit={submit} className="space-y-3" noValidate aria-label="Add weld">
        {error && <p role="alert" className="text-sm text-fieldiq-text">{error}</p>}
        <NdtRefusalNotice refusal={refusal} />
        <NdtField label="Weld number">
          <input className={`${NDT_INPUT} h-12 text-base`} value={v.weld_number} onChange={set('weld_number')} autoFocus />
        </NdtField>
        <NdtField label="Welder">
          <select className={`${NDT_INPUT} h-12 text-base`} value={v.welder_id} onChange={set('welder_id')}>
            <option value="">Not recorded</option>
            {(welders.data?.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.welderStamp ? ` (${w.welderStamp})` : ''}
              </option>
            ))}
          </select>
        </NdtField>
        <div className="grid grid-cols-2 gap-3">
          <NdtField label={unit ? `Diameter (${unit})` : 'Diameter'}>
            <input className={`${NDT_INPUT} h-12 text-base`} inputMode="decimal" value={v.diameter} onChange={set('diameter')} />
          </NdtField>
          <NdtField label={unit ? `Wall (${unit})` : 'Wall'}>
            <input className={`${NDT_INPUT} h-12 text-base`} inputMode="decimal" value={v.wall} onChange={set('wall')} />
          </NdtField>
          <NdtField label="Process">
            <select className={`${NDT_INPUT} h-12 text-base`} value={v.process} onChange={set('process')}>
              <option value="">Not recorded</option>
              {(cat?.weldProcesses ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Position">
            <select className={`${NDT_INPUT} h-12 text-base`} value={v.position} onChange={set('position')}>
              <option value="">Not recorded</option>
              {(cat?.weldPositions ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </NdtField>
        </div>
        <NdtField label="Weld date">
          <input type="date" className={`${NDT_DATE_INPUT} h-12 text-base`} value={v.weld_date} onChange={set('weld_date')} />
        </NdtField>
        <button type="submit" className={PT_FIELD_PRIMARY} disabled={add.isPending}>
          Save weld
        </button>
      </form>
    </PtFieldCard>
  );
}

function WeldCard({ jobId, weld }: { jobId: string; weld: NdtFieldWeld }) {
  const qual = weld.welder_qualification;
  return (
    <Link to={`/field/ndt/jobs/${jobId}/welds/${weld.id}`} className="block" data-testid="ndt-field-weld">
      <PtFieldCard className="flex min-h-[72px] items-center gap-3 px-4 py-3 active:bg-fieldiq-dark/50">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-extrabold text-white">{weld.weld_number}</span>
            <PtFieldBadge skin={ndtWeldStandingSkin(weld.standing)} />
            {qual?.status === 'not_qualified' && <PtFieldBadge skin={ndtQualStatusSkin(qual.status)} />}
          </div>
          <p className="mt-0.5 truncate text-sm text-fieldiq-text">
            {weld.welder_name ? `${weld.welder_name}${weld.welder_stamp ? ` (${weld.welder_stamp})` : ''}` : 'No welder recorded'}
          </p>
          <p className="text-xs text-fieldiq-muted">{weld.exam_count === 0 ? 'Not examined' : `${weld.exam_count} exam${weld.exam_count === 1 ? '' : 's'}`}</p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function NdtFieldJobScreen() {
  const { id } = useParams<{ id: string }>();
  const query = useNdtFieldJob(id);
  const [adding, setAdding] = useState(false);
  const job = query.data?.data ?? null;
  const meta = query.data?.meta;
  const welds = job?.welds ?? [];

  return (
    <PtFieldPage title={job?.job_number ?? 'NDT job'} backTo="/field/ndt" testId="ndt-field-job">
      <PtFieldQueryState query={query} subject="this job">
        {!job ? (
          <PtFieldEmpty>This job was not found.</PtFieldEmpty>
        ) : (
          <>
            <PtFieldCard className="px-4 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <PtFieldBadge skin={ndtJobStatusSkin(job.status)} />
                <span className="text-sm text-fieldiq-muted">{job.acceptance_code_label || 'No acceptance code'}</span>
              </div>
              <p className="mt-2 text-base font-bold text-white">{job.customer_name || '—'}</p>
              <p className="text-sm text-fieldiq-text">{ndtFieldJobWhere(job)}</p>
              <p className="mt-1 text-xs text-fieldiq-muted">{ndtFieldWeldProgress(job.weld_counts)}</p>
              {job.scope && <p className="mt-2 text-sm text-fieldiq-text">{job.scope}</p>}
            </PtFieldCard>

            <NdtFieldJobTickets job={job} />

            <PtFieldSectionTitle>Welds · {welds.length}</PtFieldSectionTitle>
            <div className="space-y-2">
              {adding ? (
                <AddWeldForm jobId={job.id} unit={meta?.units?.length ?? null} today={meta?.today ?? null} onDone={() => setAdding(false)} />
              ) : (
                <button type="button" className={PT_FIELD_PRIMARY} onClick={() => setAdding(true)}>
                  <Plus className="size-5" aria-hidden="true" />
                  Add weld
                </button>
              )}
              {adding && (
                <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setAdding(false)}>
                  Close
                </button>
              )}
              {welds.length === 0 ? (
                <PtFieldEmpty>No welds are recorded on this job yet.</PtFieldEmpty>
              ) : (
                welds.map((w) => <WeldCard key={w.id} jobId={job.id} weld={w} />)
              )}
            </div>
          </>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
